import type { TDateISO } from "~/@types/TDateISO";

import { addDays } from "date-fns/addDays";
import { addWeeks } from "date-fns/addWeeks";
import { differenceInDays } from "date-fns/differenceInDays";
import { endOfISOWeek } from "date-fns/endOfISOWeek";
import { getISOWeek } from "date-fns/getISOWeek";
import { getISOWeeksInYear } from "date-fns/getISOWeeksInYear";
import { getISOWeekYear } from "date-fns/getISOWeekYear";
import { startOfISOWeek } from "date-fns/startOfISOWeek";

import { ProduceWeekDisplayAnchor } from "~/gql/graphqlBase.generated";
import siteTimezone from "~/helpers/rates/siteTimezone";
import { stringFormatDate, toLocalDateTimeIso } from "~/helpers/timestampFormat";
import i18n from "~/i18n/i18n";

export type DateUnit = "days" | "weeks";

export type LeadTimeDirection = "finishToStart" | "startToFinish";

export interface DateRange {
	end: Date;
	start: Date;
}

export const isoWeek = (date: Date): number => getISOWeek(date);

export const isoWeekYear = (date: Date): number => getISOWeekYear(date);

export const isoWeeksInYear = (date: Date): number => getISOWeeksInYear(date);

export const weekToDateRange = (week: number, year: number): DateRange => {
	const week1Monday = startOfISOWeek(new Date(year, 0, 4));
	const start = addWeeks(week1Monday, week - 1);
	return { end: endOfISOWeek(start), start };
};

const WEEK_ANCHOR_OFFSET_DAYS: Record<ProduceWeekDisplayAnchor, number> = {
	[ProduceWeekDisplayAnchor.Monday]: 0,
	[ProduceWeekDisplayAnchor.Saturday]: -2,
	[ProduceWeekDisplayAnchor.Sunday]: -1
};

export const weekAnchorDate = (isoYear: number, isoWeekNumber: number, anchor: ProduceWeekDisplayAnchor): Date =>
	addDays(weekToDateRange(isoWeekNumber, isoYear).start, WEEK_ANCHOR_OFFSET_DAYS[anchor]);

export const weekAnchorDateIso = (isoYear: number, isoWeekNumber: number, anchor: ProduceWeekDisplayAnchor): TDateISO =>
	toLocalDateTimeIso(weekAnchorDate(isoYear, isoWeekNumber, anchor));

// Timezone-safe: reads calendar digits directly, avoiding new Date(iso)'s local-zone reinterpretation.
export const parseDateOnly = (value: string | null | undefined): Date | null => {
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
	if (!match) return null;
	return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

export const calendarDateInZone = (instant: Date, timeZone: string): Date =>
	parseDateOnly(siteTimezone.dateOnly(instant.toISOString(), timeZone)) ?? instant;

export const isoWeekInZone = (instant: Date, timeZone: string): number =>
	isoWeek(calendarDateInZone(instant, timeZone));

export const isoWeekYearInZone = (instant: Date, timeZone: string): number =>
	isoWeekYear(calendarDateInZone(instant, timeZone));

export const anchorDateForWeek = (
	dateOnlyIso: string | null | undefined,
	anchor: ProduceWeekDisplayAnchor
): TDateISO | null => {
	const parsed = parseDateOnly(dateOnlyIso);
	return parsed ? weekAnchorDateIso(isoWeekYear(parsed), isoWeek(parsed), anchor) : null;
};

export const formatAnchoredWeekDate = (
	dateOnlyIso: string | null | undefined,
	anchor: ProduceWeekDisplayAnchor
): string => {
	const anchored = anchorDateForWeek(dateOnlyIso, anchor);
	return anchored ? stringFormatDate(anchored) : "";
};

export const shiftDate = (date: Date, amount: number, unit: DateUnit): Date =>
	unit === "weeks" ? addWeeks(date, amount) : addDays(date, amount);

export const leadTimeDate = (anchor: Date, duration: number, unit: DateUnit, direction: LeadTimeDirection): Date => {
	const magnitude = Math.abs(duration);
	return shiftDate(anchor, direction === "finishToStart" ? -magnitude : magnitude, unit);
};

export const currentWeekRange = (now: Date, weeks: number): DateRange => {
	const start = weekToDateRange(isoWeek(now), isoWeekYear(now)).start;
	return { end: leadTimeDate(start, weeks, "weeks", "startToFinish"), start };
};

export const daysBetween = (from: Date, to: Date): number => differenceInDays(to, from);

export const formatDayGap = (from: Date, to: Date): string => {
	const total = daysBetween(from, to);
	const magnitude = Math.abs(total);
	if (magnitude < 7) {
		return i18n.t("common:grid.ribbon.tools.dateTool.dayGap", { count: total });
	}
	const weeks = Math.trunc(magnitude / 7);
	const days = magnitude % 7;
	return i18n.t("common:grid.ribbon.tools.dateTool.dayGapWithWeeks", { days, total, weeks });
};
